import { useToggle } from 'hooks/useToggle';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FilmStrip,
  PauseCircle,
  PlayCircle,
  SpeakerSimpleHigh,
  SpeakerSimpleX,
  GearSix,
  Trash,
  ArrowUUpLeft,
  MagicWand,
  Sparkle,
  Copy,
} from '@phosphor-icons/react';
import tw, { styled } from 'twin.macro';
import { AdEditorSideBarTabs } from 'views/ad-builder/AdBuilder';
import ToolTip from '../tooltip/ToolTip';

import { useSymplCookie } from 'hooks/symplCookie';
import useAdEditorContext from 'hooks/context/ad-editor-context';
import { AdCreativeType, AdPlacement } from 'types/adTypes';
import { fireEvent } from 'utils/eventHelper';
import useNavigationContext from 'hooks/context/nav-context';
import { popConfetti } from 'utils/baseHelpers';
import { TextT, Rectangle } from '@phosphor-icons/react';
import {
  variantShowsDrawingTools,
  variantShowsServerTemplate,
} from 'utils/templateFieldHelpers';
import useVideoGenContext from 'hooks/context/video-gen-context';
import {
  CANNOT_EDIT_BRING_TO_LIFE,
  carriesBringToLifeClip,
  wizardEntryFor,
} from 'components/video-gen/wizardEntry';
import { getOpenAiThreadsResponse } from 'utils/openAiHelpers';
import { generatePromptEssenceShort } from 'utils/vacancy-generator/prompts';
import { AIEditorPopup } from 'components/form/text-editor/TextEditorAIPlugin';
import useDefocusHandler from 'hooks/defocus';
import { useToastNotifications } from 'hooks/notificationHooks';
import { ToastTypes } from 'types/notificationTypes';
import { v4 as uuid } from 'uuid';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { duplicateButtonLabel } from './toolbarWording';

export enum ToolbarOrientation {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

interface AdEditorToolbarProps {
  orientation?: ToolbarOrientation;
  divide?: boolean;
  customButtons?: JSX.Element[];
  setActiveTab?: (value?: AdEditorSideBarTabs) => void;
}

const AdEditorToolbar: React.FC<AdEditorToolbarProps> = ({
  orientation = ToolbarOrientation.VERTICAL,
  divide = true,
  customButtons,
  setActiveTab,
}) => {
  const [usedAiGen, setUsedAiGen] = useSymplCookie('usedAiGen');
  const [activeThread, setActiveThread] = useState<string>();

  const [showAI, setShowAI] = useState(false);
  const [prompt, setPrompt] = useState('');

  const { activeVacancy } = useNavigationContext();
  const {
    svgStack,
    currentVariant,
    payload,
    updateCurrentVariant,
    uploadMode,
    setUploadMode,
    deleteCurrentVariant,
    videoRef,
    addCanvasElement,
    undoChange,
    duplicateCurrentVariant,
    setAiWritingOn,
    pageHasUndo,
    variants,
  } = useAdEditorContext();
  const { addToast } = useToastNotifications();

  // SYM-6183: a setter, not only a toggle, so a refused unmute can put the
  // button back to the truth.
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  const [isVideoPlaying, toggleIsVideoPlaying] = useToggle(true);
  const [isGenerating, toggleIsGenerating] = useToggle(false);
  const aiPromptInputRef = useRef<HTMLInputElement | null>(null);

  const isVideo = useMemo(
    () => currentVariant?.creative_type === AdCreativeType.VIDEO,
    [currentVariant?.creative_type]
  );

  const isFeed = useMemo(
    () => currentVariant?.placement === AdPlacement.FEED,
    [currentVariant?.placement]
  );

  /**
   * SYM-5928. On a v2 template the canvas is still mounted, just covered by the server render, so
   * the drawing tools do not fail -- they succeed invisibly and rewrite the variant's svg. Hide
   * them rather than disable them: a disabled button still says "this is a thing you could do
   * here", and on a server-rendered template it is not.
   *
   * Gear, Duplicate and Delete stay: all three work on a v2 variant.
   *
   * SYM-6227. A video ad is the same case and was never added to it: the
   * drawing tools mean "draw something here", and a video is not a canvas you
   * draw on. The text on a Track 1 video comes from the wizard and is drawn
   * by the renderer. So the two buttons ask one tested rule for both cases,
   * variantShowsDrawingTools. Gear and Undo were already off on a video
   * (below); Duplicate and Delete stay, both act on the variant itself.
   */
  const showsServerTemplate = useMemo(
    () => variantShowsServerTemplate(currentVariant?.template),
    [currentVariant?.template]
  );
  const showsDrawingTools = useMemo(
    () =>
      variantShowsDrawingTools({
        template: currentVariant?.template,
        creative_type: currentVariant?.creative_type,
      }),
    [currentVariant?.template, currentVariant?.creative_type]
  );

  /*
   * SYM-6183. Pressing unmute did nothing, and kept doing nothing.
   *
   * Two faults, both here. The sound was applied AFTER play() was asked for,
   * so the element was still silent at the moment playback started. And when
   * the browser refused that playback, the handler forced the sound back off
   * and replayed without telling React, so the button went on showing the
   * video as unmuted while no sound came out. That disagreement survives a
   * reload, because the reload starts muted and the next press hits the same
   * refusal.
   *
   * Browsers refuse to start playback with sound unless it follows a real
   * click, so falling back to silence is right. Saying so is the part that
   * was missing.
   */
  useEffect(() => {
    if (!isVideo) return;

    // Set in VideoContainer; absent until the element is on the page.
    const videoElement = videoRef.current;
    if (!videoElement) return;

    videoElement.muted = isVideoMuted;

    if (!isVideoPlaying) {
      videoElement.pause();
      return;
    }

    const started = videoElement.play();
    if (started === undefined) return;

    started.catch(() => {
      videoElement.muted = true;
      setIsVideoMuted(true);
      videoElement.play().catch((e) => console.warn(e));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideoMuted, isVideoPlaying, currentVariant?.path]);

  // If the uploade mode changes, reset the controls
  useEffect(() => {
    if (!isVideo) return;
    if (!isVideoPlaying) toggleIsVideoPlaying();
    if (!isVideoMuted) setIsVideoMuted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadMode]);

  const editorIsEmpty = useMemo(
    () => !currentVariant?.text,
    [currentVariant?.text, currentVariant]
  );

  // Starts as true: if you come load up a TextEditor with text in it,
  // there will be no thread_id associated and the assistant should receive the current value
  // HOWEVER, if the editor is empty, it should not be dirty?
  const [editorDirty, setEditorDirty] = useState(true);

  useEffect(() => {
    if (!isGenerating) {
      setActiveThread(undefined);
    }
  }, [currentVariant]);

  useEffect(() => {
    if (!isGenerating) {
      setEditorDirty(true);
    }
  }, [currentVariant?.text]);

  const defaultPrompt = useMemo(() => {
    return payload && generatePromptEssenceShort(payload);
  }, [payload]);

  const aiFocusRef = useRef(null);
  useDefocusHandler(aiFocusRef, () => setShowAI(false));

  const generateCopy = async () => {
    // TODO: check to reincorporate this, might be in the wrong spot now
    if (!activeVacancy || !payload || isGenerating) return;

    setShowAI(false);

    // You should use the default prompt if there is one and the editor is empty or there is no custom prompt
    const useDefaultPrompt =
      (editorIsEmpty || !prompt.length) && !!defaultPrompt;

    /*
     * 1. The text editor is empty and there is a default prompt:
     *    => Generate text
     * 2. The text editor is not empty => open popup
     * 3. The text editor is empty, but there is no default prompt => open popup
     */
    const messages = useDefaultPrompt
      ? defaultPrompt!
      : [
          ...(editorDirty
            ? [
                {
                  role: 'assistant' as const,
                  content: `The current value is: ${currentVariant?.text}.`,
                },
              ]
            : []),
          { role: 'user' as const, content: prompt },
        ];

    setShowAI(false);
    toggleIsGenerating();
    // Auto-save: this ad is not stored while the answer arrives (AB-5).
    setAiWritingOn(currentVariant?.uuid ?? null);

    let answerFailed = false;
    /** The ad text as it stands, to put back if the answer fails. */
    const textBefore = currentVariant?.text ?? '';

    try {
      const { thread_id } = await getOpenAiThreadsResponse({
        onChange: (value) => updateCurrentVariant('text', value),
        identifier: 'ad_builder',
        messages: messages,
        thread_id: useDefaultPrompt ? undefined : activeThread, // If the default prompt is used, a new thread should start
        vacancy_id: activeVacancy,
        stream: true,
      });

      setActiveThread(thread_id);
    } catch {
      /*
       * A failed answer leaves the ad text as it was. The stream reader now
       * reports the server's "error:" line as a failure instead of handing it
       * on as text; without this the half answer stayed in the ad and nothing
       * was said (review round 4, A6).
       */
      answerFailed = true;
      updateCurrentVariant('text', textBefore);
      addToast({
        type: ToastTypes.ERROR,
        id: uuid(),
        date: new Date(),
        title: 'The AI assistant could not answer.',
        description: 'Your text was not changed.',
        visible: true,
      } as Parameters<typeof addToast>[0]);
    } finally {
      toggleIsGenerating();
      setAiWritingOn(null);
      setEditorDirty(false);
      setPrompt('');
    }

    // No confetti, no "has used AI", no usage event for an answer that failed
    // (review round 5, C5).
    if (answerFailed) return;

    if (!usedAiGen) {
      popConfetti();
      setUsedAiGen(true);
    }

    fireEvent('ai_ad_toolbar');
  };

  const handleClick = () => {
    editorIsEmpty && !!defaultPrompt ? generateCopy() : setShowAI(!showAI);
  };

  const { videoGen, copyVideo, videoLocks } = useVideoGenContext();

  const hasT1 = useFeatureFlagEnabled('video-track-1-scenario');
  // TODO: drop `ai-video` fallback once T1 rollout is complete and the legacy flag is retired.
  const hasLegacyAiVideo = useFeatureFlagEnabled('ai-video');
  const showT1Entry = hasT1 || hasLegacyAiVideo;

  /*
   * SYM-6184, then SYM-6185. When the sparkle shows, and what it says.
   *
   * It used to be shown only when the ad's video carried a thread id, the
   * legacy assistant's conversation. Track 1 writes none, so the entry point
   * did not exist for the flow it was built for (SYM-6184): on Track 1 it is
   * shown on any ad that carries a video.
   *
   * What it DOES changed on 19 September 2026 (SYM-6185, Steven): it opens the
   * video that is on this ad again, with its slides, pictures and look, so one
   * wrong word no longer means starting over. It no longer makes a fresh one;
   * for that you add a new ad, where the empty ad offers "Generate a video
   * using AI". The label says what it does, as it has each time it changed
   * (SYM-6184 "Make another video", SYM-6208 "Replace with new video").
   */
  const adCarriesAVideo = isVideo && !!currentVariant?.path;
  const videoEntryReopens = hasT1;
  const showVideoEntry = videoEntryReopens
    ? adCarriesAVideo
    : hasLegacyAiVideo && !!currentVariant?.path?.thread_id;
  /*
   * SYM-6185, Steven 19 September 2026: on a Bring to Life clip the sparkle
   * stays where it is but is greyed out and says why. Greyed out and not
   * hidden, unlike the drawing tools above: here the customer would look for
   * it, and the answer ("make a new one") is worth giving.
   */
  const videoEntryRefused =
    videoEntryReopens &&
    wizardEntryFor({
      adIsVideo: isVideo,
      adIsBringToLife: carriesBringToLifeClip(currentVariant, variants),
      resourceId: currentVariant?.path?.id,
    }).kind === 'bring-to-life';
  const videoEntryLabel = videoEntryRefused
    ? CANNOT_EDIT_BRING_TO_LIFE
    : videoEntryReopens
    ? 'Edit this video'
    : 'Update video using AI';

  /*
   * Copy keeps the old question, because it genuinely needs the answer:
   * copyVideo gives up immediately without a thread id
   * (`VideoGenContext.tsx:385`). Offering it on a Track 1 video would be a
   * button that silently does nothing.
   */
  const showCopyVideo = showT1Entry && !!currentVariant?.path?.thread_id;

  return (
    <Wrapper orientation={orientation}>
      {customButtons ? (
        customButtons.map((button, index) => (
          <ButtonItem key={index} divide={divide}>
            {button}
          </ButtonItem>
        ))
      ) : (
        <>
          {(showVideoEntry || showCopyVideo) &&
            !videoLocks.creatomate && ( // SYM-5391
              <ButtonItem divide={divide}>
                {showVideoEntry && (
                  <ToolTip text={videoEntryLabel} placement="right" arrow>
                    {videoEntryRefused ? (
                      // The span takes the hover: a disabled button takes none,
                      // and the reason has to be readable.
                      <span
                        tw="flex grow justify-center cursor-not-allowed"
                        data-testid="video-entry-refused"
                      >
                        <AdEditorButton
                          type="button"
                          disabled
                          isDisabled
                          aria-label={videoEntryLabel}
                        >
                          <Sparkle
                            weight="fill"
                            size={32}
                            tw="p-1 text-gray-300"
                          />
                        </AdEditorButton>
                      </span>
                    ) : (
                      <AdEditorButton onClick={videoGen}>
                        <Sparkle
                          weight="fill"
                          size={32}
                          tw="p-1 text-indigo-600"
                        />
                      </AdEditorButton>
                    )}
                  </ToolTip>
                )}
                {showCopyVideo && (
                  <ToolTip //
                    text={`Copy video to ${
                      currentVariant?.placement === AdPlacement.FEED
                        ? `${AdPlacement.STORIES} and ${AdPlacement.REELS}`
                        : AdPlacement.FEED
                    }`}
                    placement="right"
                    arrow
                  >
                    <AdEditorButton onClick={copyVideo}>
                      <Copy weight="bold" size={32} tw="p-1 text-indigo-600" />
                    </AdEditorButton>
                  </ToolTip>
                )}
              </ButtonItem>
            )}

          <div ref={aiFocusRef} tw="relative">
            <ButtonItem divide={false} hidden={!isFeed}>
              <ToolTip text="Generate ad text using AI" placement="right" arrow>
                <AdEditorButton
                  isLoading={isGenerating}
                  isDisabled={!payload}
                  onClick={handleClick}
                >
                  <MagicWand weight="bold" size={32} tw="p-1" />
                </AdEditorButton>
              </ToolTip>
            </ButtonItem>
            {showAI && (
              <AIEditorPopup
                aiPromptInputRef={aiPromptInputRef}
                editorIsEmpty={editorIsEmpty}
                hasDefaultPrompt={!!defaultPrompt}
                onPrompt={generateCopy}
                prompt={prompt}
                setPrompt={setPrompt}
                showActionsList
              />
            )}
          </div>

          {!isVideo && (
            <>
              <ButtonItem divide={divide}>
                <ToolTip text="Advanced options" placement="right" arrow>
                  <AdEditorButton
                    onClick={() =>
                      setActiveTab?.(AdEditorSideBarTabs.VISUAL_OPTIONS)
                    }
                  >
                    <GearSix weight="bold" size={32} tw="p-1 text-gray-600" />
                  </AdEditorButton>
                </ToolTip>
              </ButtonItem>
              {!showsServerTemplate && !pageHasUndo && (
                <ButtonItem divide={divide} data-testid="ad-drawing-undo">
                  <ToolTip text="Undo changes" placement="right" arrow>
                    <AdEditorButton
                      isDisabled={svgStack.isEmpty()}
                      onClick={undoChange}
                    >
                      <ArrowUUpLeft weight="bold" size={32} tw="p-1" />
                    </AdEditorButton>
                  </ToolTip>
                </ButtonItem>
              )}
            </>
          )}

          {isVideo && (
            <>
              <ButtonItem divide={divide}>
                {/*
                  SYM-6208. "Change video" sat beside the sparkle and read like
                  the same offer, so it says what it opens.
                
                  It also has to actually open it. Pressing this only asked for
                  the visual-options tab, and that tab is normally the one
                  already showing -- so on a video ad the button did nothing at
                  all, which is how it was found on QA on 15 September (T1-36).
                  It now opens the library the same way the video refusal does.

                  And it says "replace" beside the sparkle's "replace" when
                  there IS a video to replace, which is what it does. This
                  button shows on any video ad, including an empty one, where
                  there is nothing to replace and it says choose instead.
                */}
                <ToolTip
                  text={
                    adCarriesAVideo
                      ? 'Replace with video from library'
                      : 'Choose video from library'
                  }
                  placement="right"
                  arrow
                >
                  <AdEditorButton
                    onClick={() => {
                      setActiveTab?.(AdEditorSideBarTabs.VISUAL_OPTIONS);
                      setUploadMode(true);
                    }}
                  >
                    <FilmStrip
                      rotate={90}
                      weight="bold"
                      size={32}
                      tw="p-1 text-gray-600"
                    />
                  </AdEditorButton>
                </ToolTip>
              </ButtonItem>
              <ButtonItem divide={divide}>
                <ToolTip
                  text={isVideoMuted ? 'Unmute volume' : 'Mute volume'}
                  placement="right"
                  arrow
                >
                  <AdEditorButton
                    onClick={() => setIsVideoMuted((muted) => !muted)}
                  >
                    {isVideoMuted ? (
                      <SpeakerSimpleX
                        weight="bold"
                        size={32}
                        tw="p-1 text-gray-600"
                      />
                    ) : (
                      <SpeakerSimpleHigh
                        weight="bold"
                        size={32}
                        tw="p-1 text-gray-600"
                      />
                    )}
                  </AdEditorButton>
                </ToolTip>
              </ButtonItem>
              <ButtonItem divide={divide}>
                <ToolTip
                  text={isVideoPlaying ? 'Pause video' : 'Play video'}
                  placement="right"
                  arrow
                >
                  <AdEditorButton onClick={toggleIsVideoPlaying}>
                    {isVideoPlaying ? (
                      <PauseCircle
                        weight="bold"
                        size={32}
                        tw="p-1 text-gray-600"
                      />
                    ) : (
                      <PlayCircle
                        weight="bold"
                        size={32}
                        tw="p-1 text-gray-600"
                      />
                    )}
                  </AdEditorButton>
                </ToolTip>
              </ButtonItem>
            </>
          )}

          {showsDrawingTools && (
            <>
              <ButtonItem divide={divide}>
                <ToolTip text="Add text" placement="right" arrow>
                  <AdEditorButton onClick={() => addCanvasElement('text')}>
                    <TextT size="32" weight="bold" tw="p-1 text-gray-600" />
                  </AdEditorButton>
                </ToolTip>
              </ButtonItem>

              <ButtonItem divide={divide}>
                <ToolTip text="Add a rectangle" placement="right" arrow>
                  <AdEditorButton onClick={() => addCanvasElement('rectangle')}>
                    <Rectangle size="32" weight="bold" tw="p-1 text-gray-600" />
                  </AdEditorButton>
                </ToolTip>
              </ButtonItem>
            </>
          )}

          <ButtonItem divide={divide} data-testid="ad-duplicate">
            {/* SYM-6289. "Duplicate video" on a video ad; see toolbarWording.ts. */}
            <ToolTip
              text={duplicateButtonLabel(isVideo)}
              placement="right"
              arrow
            >
              <AdEditorButton onClick={duplicateCurrentVariant}>
                <Copy weight="bold" size={32} tw="p-1 text-gray-600" />
              </AdEditorButton>
            </ToolTip>
          </ButtonItem>
          <ButtonItem divide={divide} data-testid="ad-delete">
            <ToolTip text="Delete variant" placement="right" arrow>
              <AdEditorButton onClick={deleteCurrentVariant}>
                <Trash weight="bold" size={32} tw="p-1 text-gray-600" />
              </AdEditorButton>
            </ToolTip>
          </ButtonItem>
        </>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.ul<{
  orientation: ToolbarOrientation;
}>`
  ${tw`
    flex flex-row justify-between items-baseline border rounded-md shadow-md py-2
    sm:(justify-center border-0)
  `}
  ${({ orientation }) =>
    orientation === ToolbarOrientation.HORIZONTAL
      ? tw`w-fit mx-auto p-1 sm:(flex-row space-x-2)`
      : tw`px-1 sm:(flex-col space-y-2)`}
`;

const ButtonItem = styled.li<{
  divide: boolean;
}>`
  ${tw`bg-none`}
  ${({ divide }) =>
    divide &&
    tw`first-of-type:(border-r py-1 border-gray-200 sm:(border-r-0 border-b))
    last-of-type:(border-l py-1 border-gray-200 sm:(border-l-0 border-t))`}
`;

export const AdEditorButton = styled.button<{
  isActive?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
}>`
  ${tw`rounded-full p-2 grow flex text-gray-600 justify-center sm:p-1 hover:bg-gray-100`}
  ${({ isActive, isDisabled }) => isActive && !isDisabled && tw`bg-gray-100`}
  ${({ isLoading }) => isLoading && tw`cursor-wait`}
  ${({ isDisabled }) =>
    isDisabled && tw`cursor-not-allowed pointer-events-none text-gray-200`}
  ${({ isLoading, isDisabled }) =>
    (isLoading || isDisabled) && tw`text-gray-300 hover:bg-white`}
`;

export default AdEditorToolbar;
